// @givmo/app-charity — App root: providers + router.
//
// Composition order (outer → inner):
//   QueryClientProvider  — TanStack Query client at the root.
//   AuthProvider         — tracks the live Firebase session.
//   RouterProvider       — receives the LIVE auth state via the `context` prop (the router itself was
//                          created with a placeholder context in createCharityRouter). A post-commit
//                          uid effect clears the cache and re-runs route guards.
//
// `App` takes its dependencies (auth instance, query client, router) as props so it can be rendered
// in tests with an in-memory router + a mock Firebase auth, with no real Firebase/network.
// (Mirrors the admin app's App root.)

import { useLayoutEffect, useRef, useState } from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import type { Auth } from 'firebase/auth';
import type { SuiteSessionTransport } from '@givmo/auth';
import { BoardRuntimeProvider, purgeDirectorDraftsNotOwnedBy } from '@givmo/board';
import { AuthProvider, useAuth } from './auth/auth-context';
import { AuthSplash } from './components/auth-splash';
import { CHARITY_BOARD_RUNTIME } from './features/board/charity-board-operations';
import type { createCharityRouter } from './router/router';

export type CharityRouter = ReturnType<typeof createCharityRouter>;

export interface AppProps {
  auth: Auth;
  queryClient: QueryClient;
  router: CharityRouter; /**
   * The suite-session transport (AS-SUITE4), threaded to `AuthProvider`. `main.tsx` builds it;
   * omitted in tests, which disables the rail so the tree behaves exactly as it did before.
   */
  suiteSession?: SuiteSessionTransport;
}

/**
 * Inner tree (inside AuthProvider) so it can read the live auth state and feed it to the router.
 *
 * Gates the router on `isReady`: until the first `onAuthStateChanged` emission resolves, render the
 * splash and DO NOT mount `RouterProvider`. This closes the cold-load auth race — the `beforeLoad`
 * guard never runs against an unresolved session, so a hard refresh into a protected route with a
 * persisted session lands on that route (not /login-home), while a genuinely signed-out cold-load
 * still redirects to /login-home once ready.
 */
function RoutedApp({ router, queryClient }: { router: CharityRouter; queryClient: QueryClient }) {
  const auth = useAuth();
  const uid = auth.user?.uid ?? null;
  const previousUidRef = useRef(uid);
  const [cacheEpoch, setCacheEpoch] = useState(0);

  // The account boundary for a director's unsent words (`@givmo/board`'s director drafts, kept in
  // this tab's sessionStorage across a forced sign-out): as an account signs in, every OTHER
  // account's drafts go, here in render, BEFORE anything renders for it. Signing out keeps them, so
  // the same account coming back finds its words; a different one never sees them.
  const draftsOwnerRef = useRef<string | null>(null);
  if (uid !== null && uid !== draftsOwnerRef.current) {
    purgeDirectorDraftsNotOwnedBy(uid);
    draftsOwnerRef.current = uid;
  }

  useLayoutEffect(() => {
    if (previousUidRef.current === uid) return;
    const hadUser = previousUidRef.current !== null;
    previousUidRef.current = uid;

    // Firebase identity is a hard cache boundary: none of the charity query keys include the uid.
    // This effect runs after RouterProvider receives the new auth context, so invalidating here also
    // makes external/session-expiry sign-outs re-run guards against the committed user state.
    queryClient.clear();
    // Clearing destroys the cache but an already-mounted QueryObserver can retain its last result.
    // Remount the route consumers before paint so no prior user's organization strings survive.
    // Only a transition AWAY from a signed-in user needs the remount — on null->user (cold-load
    // resolution, fresh sign-in) no prior user's observers exist, and bumping the epoch there
    // double-mounts the first route before paint (double-firing mount-time side effects).
    if (hadUser) {
      setCacheEpoch((epoch) => epoch + 1);
    }
    void router.invalidate();
  }, [queryClient, router, uid]);

  if (!auth.isReady) {
    return <AuthSplash />;
  }
  return <RouterProvider key={cacheEpoch} router={router} context={{ auth }} />;
}

export function App({ auth, queryClient, router, suiteSession }: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider auth={auth} suiteSession={suiteSession}>
        {/* Board's runtime: the officer port, the director port (a director is this console's own
            signed-in account, CBG-SEC2), the mount and the class words. */}
        <BoardRuntimeProvider {...CHARITY_BOARD_RUNTIME}>
          <RoutedApp router={router} queryClient={queryClient} />
        </BoardRuntimeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
